import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { getYearCalendar, transformCalendarData } from 'services/calendarService';
import type { WorkCalendarDay } from 'models/calendar';

type FullWorkCalendarDay = Omit<WorkCalendarDay, 'name'> & { name?: string };
type FullWorkCalendar = FullWorkCalendarDay[];

interface HolidayResponse {
  holidays: FullWorkCalendar;
  loading: boolean;
}

export const useHolidays = (dateRange: { start: Date; end: Date }): HolidayResponse => {
  const [holidays, setHolidays] = useState<FullWorkCalendar>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchHolidays = async (): Promise<void> => {
      setLoading(true);
      try {
        const startYear = dateRange.start.getFullYear();
        const endYear = dateRange.end.getFullYear();
        const years = Array.from(
          { length: endYear - startYear + 1 },
          (_, i) => startYear + i
        );

        const calendarData = await Promise.all(
          years.map(async (year) => {
            const yearData = await getYearCalendar(year);
            return transformCalendarData(yearData);
          })
        );

        const daysMap = new Map<string, WorkCalendarDay>();
        calendarData.flat().forEach((day) => {
          daysMap.set(dayjs(day.date).format('YYYY-MM-DD'), day);
        });

        // Формируем массив дат для периода
        const dates: string[] = [];
        const currentDate = new Date(dateRange.start);
        while (currentDate <= dateRange.end) {
          dates.push(dayjs(currentDate).format('YYYY-MM-DD'));
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Формируем результат
        const holidayStatuses: FullWorkCalendar = dates.map((date) => {
          const dateKey = date;
          const dayInfo: FullWorkCalendarDay = daysMap.get(dateKey) ?? {
            date: dayjs(dateKey),
            isHoliday: false,
            isShortDay: false,
          };

          return dayInfo;
        });

        setHolidays(holidayStatuses);
      } catch (error) {
        console.error('Failed to fetch holidays:', error);
        setHolidays([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchHolidays();
  }, [dateRange.start, dateRange.end]);

  return { holidays, loading };
};
